// Thành phần giao diện dùng chung cho Dashboard kinh doanh
import { Avatar, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { Component, Fragment, useState, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, CheckCircle2, ChevronRight, Info, Minus, OctagonAlert } from 'lucide-react';
import { fmtRate, ratio, type AlertLevel, type Insight } from './insights';

/** Mức thay đổi so với kỳ trước: phần trăm (số lượng) hoặc điểm phần trăm (tỷ lệ) */
export function Delta({ cur, prev, kind = 'percent', goodWhenUp = true, compact, neutral }: {
  cur: number | null; prev: number | null; kind?: 'percent' | 'points'; goodWhenUp?: boolean; compact?: boolean;
  /** Tăng/giảm không mang nghĩa tốt/xấu (ví dụ chi phí) → màu trung tính */
  neutral?: boolean;
}) {
  if (cur === null || prev === null) return <span className="text-xs text-slate-300">—</span>;
  const diff = cur - prev;
  if (Math.abs(diff) < 0.05) return <span className="inline-flex items-center gap-0.5 text-xs text-slate-400"><Minus size={12} />{compact ? '' : ' không đổi'}</span>;
  const up = diff > 0;
  let text: string;
  if (kind === 'points') text = `${Math.abs(diff).toFixed(1).replace('.', ',')} điểm`;
  else if (prev === 0) text = 'mới';
  else text = `${Math.abs(Math.round((diff / prev) * 100))}%`;
  const good = up === goodWhenUp;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${neutral ? 'text-slate-500' : good ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{text}
    </span>
  );
}

export const Panel = ({ title, info, extra, children, className = '', accent }: {
  title: React.ReactNode; info?: string; extra?: React.ReactNode; children: React.ReactNode; className?: string; accent?: string;
}) => (
  <section className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`} style={accent ? { borderTop: `3px solid ${accent}` } : undefined}>
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="flex min-w-0 items-center gap-1.5 text-[15px] font-semibold text-slate-800">
        {title}
        {info && <Tooltip title={info}><Info size={14} className="shrink-0 text-slate-400" /></Tooltip>}
      </h2>
      {extra}
    </div>
    {children}
  </section>
);

export const SubTitle = ({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) => (
  <div className="mb-2 mt-4 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
    <span>{children}</span>{right && <span className="font-normal normal-case tracking-normal">{right}</span>}
  </div>
);

export const MiniStat = ({ label, value, delta, title }: { label: string; value: React.ReactNode; delta?: React.ReactNode; title?: string }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2.5" title={title}>
    <div className="truncate text-xs text-slate-500">{label}</div>
    <div className="mt-0.5 flex items-baseline justify-between gap-2">
      <span className="text-lg font-bold leading-tight text-slate-800">{value}</span>
      {delta}
    </div>
  </div>
);

/** Danh sách thanh ngang có nhãn — dùng cho top kênh / dịch vụ */
export const BarList = ({ items, color, empty = 'Chưa có dữ liệu', collapseZero }: {
  items: { key: string; label: string; value: number; display: string; title?: string; href?: string }[]; color: string; empty?: string;
  /** Gom các dòng bằng 0 sau nút "Hiện thêm" để danh sách dài (thẻ S5→S8) không kéo cao cả hàng */
  collapseZero?: boolean;
}) => {
  const [showZero, setShowZero] = useState(false);
  const max = Math.max(1, ...items.map(i => i.value));
  if (!items.length) return <div className="py-4 text-center text-sm text-slate-400">{empty}</div>;
  const zeros = collapseZero ? items.filter(i => !i.value).length : 0;
  const shown = zeros && !showZero ? items.filter(i => i.value) : items;
  return (
    <>
    <ul className="space-y-2">
      {shown.map(i => (
        <li key={i.key} title={i.title || i.label}>
          <div className="mb-0.5 flex items-center justify-between gap-2 text-[13px]">
            {i.href ? <Link to={i.href} className="truncate text-slate-600 hover:text-[#357f96] hover:underline">{i.label}</Link> : <span className="truncate text-slate-600">{i.label}</span>}
            <span className="shrink-0 font-semibold text-slate-800">{i.display}</span>
          </div>
          <span className="block h-1.5 overflow-hidden rounded-full bg-slate-100">
            <span className="block h-full rounded-full" style={{ width: `${(i.value / max) * 100}%`, background: color }} />
          </span>
        </li>
      ))}
    </ul>
    <ZeroToggle count={zeros} open={showZero} onToggle={() => setShowZero(v => !v)} />
    </>
  );
};

/** Nút hiện / ẩn các dòng chưa có số liệu */
const ZeroToggle = ({ count, open, onToggle }: { count: number; open: boolean; onToggle: () => void }) =>
  count > 0 ? (
    <button type="button" onClick={onToggle} className="mt-2 text-xs font-medium text-[#357f96] hover:underline">
      {open ? 'Ẩn các mục bằng 0' : `+ ${count} mục chưa có khách`}
    </button>
  ) : null;

const MEDAL = ['#E8B021', '#A7B0BA', '#C47F45'];

/** Bảng xếp hạng ngắn: tên, chỉ số chính, dòng phụ */
export const Leaderboard = ({ rows, color, empty = 'Chưa có dữ liệu nhân sự' }: {
  rows: { key: string | number; name: string; avatar?: string | null; main: string; sub: string }[]; color: string; empty?: string;
}) => {
  if (!rows.length) return <div className="py-4 text-center text-sm text-slate-400">{empty}</div>;
  return (
    <ol className="divide-y divide-slate-100">
      {rows.map((r, i) => (
        <li key={r.key} className="flex items-center gap-2.5 py-2">
          <span className="w-5 shrink-0 text-center text-xs font-bold" style={{ color: MEDAL[i] || '#94a3b8' }}>{i + 1}</span>
          <Avatar size={28} src={r.avatar || undefined} style={{ background: color, flexShrink: 0 }}>{r.name.trim().split(/\s+/).pop()?.[0]}</Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-slate-700">{r.name}</div>
            <div className="truncate text-xs text-slate-400">{r.sub}</div>
          </div>
          <span className="shrink-0 text-[13px] font-semibold text-slate-800">{r.main}</span>
        </li>
      ))}
    </ol>
  );
};

export const LEVEL: Record<AlertLevel, { label: string; icon: typeof AlertTriangle; cls: string; bg: string }> = {
  critical: { label: 'Nghiêm trọng', icon: OctagonAlert, cls: 'text-red-500', bg: 'bg-red-50 border-red-100' },
  warning: { label: 'Cảnh báo', icon: AlertTriangle, cls: 'text-amber-500', bg: 'bg-amber-50 border-amber-100' },
  good: { label: 'Điểm sáng', icon: CheckCircle2, cls: 'text-emerald-500', bg: 'bg-emerald-50 border-emerald-100' },
};

/** Đầu mỗi nhóm: số thứ tự, tên, mô tả, liên kết */
export const GroupHeader = ({ index, title, subtitle, color, soft, extra }: {
  index: number; title: string; subtitle: string; color: string; soft: string; extra?: React.ReactNode;
}) => (
  <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl px-4 py-3" style={{ background: soft, borderLeft: `4px solid ${color}` }}>
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: color }}>{index}</span>
    <div className="min-w-0">
      <h2 className="text-base font-bold text-slate-800">{title}</h2>
      <p className="text-xs text-slate-500">{subtitle}</p>
    </div>
    {extra && <div className="ml-auto flex items-center gap-2">{extra}</div>}
  </div>
);

/** Thẻ chỉ số chính của nhóm */
export const KpiTile = ({ label, value, delta, sub, info }: { label: string; value: React.ReactNode; delta?: React.ReactNode; sub?: React.ReactNode; info?: string }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex items-center gap-1 text-xs text-slate-500">{label}{info && <Tooltip title={info}><Info size={12} className="text-slate-400" /></Tooltip>}</div>
    <div className="mt-0.5 flex items-baseline justify-between gap-2">
      <span className="text-[22px] font-bold leading-tight text-slate-800">{value}</span>
      {delta}
    </div>
    {sub && <div className="mt-0.5 truncate text-xs text-slate-400">{sub}</div>}
  </div>
);

export const KpiGrid = ({ children, cols = 5 }: { children: React.ReactNode; cols?: 4 | 5 | 6 }) => (
  <div className={`mb-4 grid grid-cols-2 gap-2.5 md:grid-cols-3 ${cols === 6 ? 'xl:grid-cols-6' : cols === 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>{children}</div>
);

/** Cảnh báo riêng của một nhóm, hiển thị dạng thẻ ngang */
export const GroupAlerts = ({ items }: { items: Insight[] }) => {
  if (!items.length) return null;
  return (
    <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
      {items.slice(0, 6).map(a => {
        const L = LEVEL[a.level];
        const Icon = L.icon;
        return (
          <div key={a.id} className={`flex gap-2.5 rounded-lg border px-3 py-2 ${L.bg}`}>
            <Icon size={16} className={`mt-0.5 shrink-0 ${L.cls}`} />
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-slate-700">{a.title}</div>
              <div className="text-xs text-slate-500">{a.detail}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Thanh chia tỷ lệ nhiều phần + chú thích */
export const SplitBar = ({ parts }: { parts: { label: string; value: number; color: string }[] }) => {
  const total = parts.reduce((s, x) => s + x.value, 0);
  return (
    <>
      <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
        {total > 0 && parts.map(x => <span key={x.label} style={{ width: `${(x.value / total) * 100}%`, background: x.color }} />)}
      </div>
      <div className="mt-1.5 flex flex-wrap justify-between gap-2 text-xs">
        {parts.map(x => (
          <span key={x.label} className="flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-sm" style={{ background: x.color }} />{x.label}
            <b className="text-slate-800">{Math.round(x.value).toLocaleString('vi-VN')}</b>
            <span className="text-slate-400">{fmtRate(ratio(x.value, total))}</span>
          </span>
        ))}
      </div>
    </>
  );
};

/** Danh sách có màu riêng từng dòng + tỷ lệ % (kết quả cuộc gọi, trạng thái…) */
export const ShareList = ({ items, empty = "Chưa có dữ liệu", collapseZero }: { items: { key: string; label: string; value: number; color: string; title?: string; href?: string }[]; empty?: string; collapseZero?: boolean }) => {
  const [showZero, setShowZero] = useState(false);
  const total = items.reduce((s, x) => s + x.value, 0);
  if (!total) return <div className="py-6 text-center text-sm text-slate-400">{empty}</div>;
  const zeros = collapseZero ? items.filter(x => !x.value).length : 0;
  const shown = zeros && !showZero ? items.filter(x => x.value) : items;
  return (
    <>
      <div className="mb-3 flex h-3 overflow-hidden rounded-full">
        {items.filter(x => x.value > 0).map(x => (
          <Tooltip key={x.key} title={`${x.label}: ${x.value.toLocaleString('vi-VN')} (${fmtRate((x.value / total) * 100)})`}>
            <span style={{ width: `${(x.value / total) * 100}%`, background: x.color }} />
          </Tooltip>
        ))}
      </div>
      <ul className="space-y-1.5">
        {shown.map(x => (
          <li key={x.key} className="flex items-center gap-2 text-[13px]" title={x.title || x.label}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: x.color }} />
            {x.href ? <Link to={x.href} className="min-w-0 flex-1 truncate text-slate-600 hover:text-[#357f96] hover:underline">{x.label}</Link> : <span className="min-w-0 flex-1 truncate text-slate-600">{x.label}</span>}
            <b className="text-slate-800">{x.value.toLocaleString('vi-VN')}</b>
            <span className="w-12 text-right text-xs text-slate-400">{fmtRate((x.value / total) * 100)}</span>
          </li>
        ))}
      </ul>
      <ZeroToggle count={zeros} open={showZero} onToggle={() => setShowZero(v => !v)} />
    </>
  );
};

export const PALETTE = ['#1D9E75', '#378ADD', '#EF9F27', '#D4537E', '#E24B4A', '#7F77DD', '#94a3b8', '#D85A30', '#0F766E', '#888780'];

export interface ProcessStep { label: string; value: string; sub?: string; warn?: boolean; href?: string }

/** Luồng xử lý bên trong một bộ phận: đầu vào → các bước → bàn giao cho bộ phận sau */
export const ProcessStrip = ({ steps, color, soft }: { steps: ProcessStep[]; color: string; soft: string }) => (
  <div className="mb-4 rounded-xl border border-slate-200 bg-white p-2.5">
    <div className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Quy trình xử lý trong kỳ</div>
    <div className="flex items-stretch gap-1 overflow-x-auto">
      {steps.map((s, i) => {
        const body = (
          <div className="h-full min-w-[128px] rounded-lg px-2.5 py-1.5" style={{ background: i === 0 || i === steps.length - 1 ? soft : '#F8FAFC', borderLeft: i === 0 ? `3px solid ${color}` : undefined }}>
            <div className="truncate text-[11px] text-slate-500">{s.label}</div>
            <div className="text-base font-bold text-slate-800">{s.value}</div>
            {s.sub && <div className={`truncate text-[11px] ${s.warn ? 'font-medium text-red-500' : 'text-slate-400'}`}>{s.sub}</div>}
          </div>
        );
        return (
          <Fragment key={s.label}>
            {i > 0 && <ChevronRight size={16} className="shrink-0 self-center text-slate-300" />}
            <div className="flex-1">{s.href ? <Link to={s.href} className="block h-full hover:opacity-80">{body}</Link> : body}</div>
          </Fragment>
        );
      })}
    </div>
  </div>
);

/** Chặn lỗi hiển thị trong từng nhóm: một phần lỗi dữ liệu không làm trắng cả trang */
export class SectionBoundary extends Component<{ title: string; children: ReactNode; resetKey?: unknown }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) { return { error }; }

  componentDidCatch(error: Error, info: ErrorInfo) { console.error(`[Dashboard] Lỗi hiển thị "${this.props.title}"`, error, info.componentStack); }

  componentDidUpdate(prev: { resetKey?: unknown }) {
    // dữ liệu mới về (đổi kỳ, làm mới) thì thử hiển thị lại
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <OctagonAlert size={18} className="shrink-0" />
        <span className="flex-1">Không hiển thị được phần <b>{this.props.title}</b> với dữ liệu hiện tại. Các phần khác vẫn hoạt động.</span>
        <button className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs hover:bg-red-100" onClick={() => this.setState({ error: null })}>Thử lại</button>
      </div>
    );
  }
}
